import type { MapSettings } from './types';
import { loadGoogleMaps } from './scriptLoader';

export interface AddressCoordinates {
  lat: number;
  lng: number;
}

export interface AddressSuggestion {
  placeId: string;
  description: string;
}

export interface ResolvedPlace {
  formattedAddress: string;
  coordinates: AddressCoordinates;
  placeId: string;
}

export interface AddressAutocompleteHandlers {
  onAddress: (address: string) => void;
  onCoordinates: (coords: AddressCoordinates) => void;
}

type GoogleMapsPlaces = {
  AutocompleteService: new () => {
    getPlacePredictions: (
      req: Record<string, unknown>,
      cb: (
        predictions: { place_id: string; description: string }[] | null,
        status: string
      ) => void
    ) => void;
  };
  PlacesService: new (attrContainer: HTMLDivElement) => {
    getDetails: (
      req: { placeId: string; fields: string[] },
      cb: (
        place: {
          formatted_address?: string;
          name?: string;
          geometry?: { location?: { lat: () => number; lng: () => number } };
        } | null,
        status: string
      ) => void
    ) => void;
  };
  PlacesServiceStatus: { OK: string; ZERO_RESULTS: string };
  Autocomplete?: new (
    el: HTMLInputElement,
    opts: Record<string, unknown>
  ) => {
    addListener: (event: string, handler: () => void) => { remove?: () => void } | void;
    getPlace: () => {
      formatted_address?: string;
      name?: string;
      geometry?: { location?: { lat: () => number; lng: () => number } };
    };
  };
  PlaceAutocompleteElement?: new (opts?: Record<string, unknown>) => PlaceAutocompleteWidget;
};

type PlaceAutocompleteWidget = HTMLElement & {
  placeholder?: string;
  value?: string;
  includedRegionCodes?: string[];
};

type PlacePredictionLike = {
  toPlace: () => {
    fetchFields: (opts: { fields: string[] }) => Promise<void>;
    formattedAddress?: string | null;
    displayName?: string | null;
    location?:
      | { lat: () => number; lng: () => number }
      | { lat: number; lng: number }
      | null;
  };
};

function ensurePacStyles(): void {
  if (typeof document === 'undefined') return;
  if (document.getElementById('ubus-google-pac-zindex')) return;
  const style = document.createElement('style');
  style.id = 'ubus-google-pac-zindex';
  style.textContent = `
    .pac-container { z-index: 11000 !important; pointer-events: auto !important; }
    .ubus-place-autocomplete-host { width: 100%; position: relative; }
    .ubus-place-autocomplete-host gmp-place-autocomplete,
    .ubus-place-autocomplete-host > * {
      width: 100% !important;
      display: block;
      box-sizing: border-box;
    }
  `;
  document.head.appendChild(style);
  document.addEventListener(
    'mousedown',
    (e) => {
      const t = e.target;
      if (t instanceof Element && t.closest('.pac-container')) {
        e.preventDefault();
      }
    },
    true
  );
}

function readLatLng(
  location:
    | { lat: () => number; lng: () => number }
    | { lat: number; lng: number }
    | null
    | undefined
): AddressCoordinates | null {
  if (!location) return null;
  const latRaw = location.lat;
  const lngRaw = location.lng;
  const lat = typeof latRaw === 'function' ? latRaw() : latRaw;
  const lng = typeof lngRaw === 'function' ? lngRaw() : lngRaw;
  if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;
  return { lat, lng };
}

async function getPlacesApi(settings: MapSettings): Promise<GoogleMapsPlaces | null> {
  if (!settings.googleApiKey) return null;
  const maps = await loadGoogleMaps(settings.googleApiKey);
  // Prefer dynamic library import so PlaceAutocompleteElement is available.
  const mapsWithImport = maps as typeof maps & {
    importLibrary?: (name: string) => Promise<{ PlaceAutocompleteElement?: unknown }>;
  };
  if (typeof mapsWithImport.importLibrary === 'function') {
    try {
      await mapsWithImport.importLibrary('places');
    } catch {
      // Fall through — classic places library from script URL may still exist.
    }
  }
  const places = (window.google?.maps?.places ??
    (maps as { places?: GoogleMapsPlaces }).places) as GoogleMapsPlaces | undefined;
  return places ?? null;
}

/** Fetch Google Places autocomplete predictions (AU). */
export async function fetchAddressSuggestions(
  input: string,
  settings: MapSettings
): Promise<AddressSuggestion[]> {
  const trimmed = input.trim();
  if (trimmed.length < 2) return [];

  const places = await getPlacesApi(settings);
  if (!places?.AutocompleteService) return [];

  const service = new places.AutocompleteService();
  return new Promise((resolve) => {
    service.getPlacePredictions(
      {
        input: trimmed,
        componentRestrictions: { country: 'au' },
        types: ['geocode'],
      },
      (predictions, status) => {
        if (status !== places.PlacesServiceStatus.OK || !predictions?.length) {
          resolve([]);
          return;
        }
        resolve(
          predictions.map((p) => ({
            placeId: p.place_id,
            description: p.description,
          }))
        );
      }
    );
  });
}

/** Resolve a Place ID to formatted address + lat/lng. */
export async function resolvePlaceDetails(
  placeId: string,
  settings: MapSettings
): Promise<ResolvedPlace | null> {
  const places = await getPlacesApi(settings);
  if (!places?.PlacesService || !placeId) return null;

  const attr = document.createElement('div');
  const service = new places.PlacesService(attr);

  return new Promise((resolve) => {
    service.getDetails(
      {
        placeId,
        fields: ['formatted_address', 'geometry', 'name', 'place_id'],
      },
      (place, status) => {
        if (status !== places.PlacesServiceStatus.OK || !place) {
          resolve(null);
          return;
        }
        const formatted =
          place.formatted_address?.trim() || place.name?.trim() || '';
        const location = place.geometry?.location;
        if (!formatted || !location) {
          resolve(null);
          return;
        }
        resolve({
          formattedAddress: formatted,
          coordinates: { lat: location.lat(), lng: location.lng() },
          placeId,
        });
      }
    );
  });
}

/**
 * Mount Google Place Autocomplete Widget (`PlaceAutocompleteElement`).
 * Falls back to classic `places.Autocomplete` on an input when the new widget
 * is unavailable (legacy key / Places API New not enabled).
 */
export async function mountPlaceAutocompleteWidget(
  container: HTMLElement,
  settings: MapSettings,
  handlers: AddressAutocompleteHandlers,
  options?: { placeholder?: string; value?: string }
): Promise<() => void> {
  ensurePacStyles();
  container.classList.add('ubus-place-autocomplete-host');
  container.replaceChildren();

  const places = await getPlacesApi(settings);
  if (!places) return () => undefined;

  const PlaceAutocompleteElementCtor = places[
    'PlaceAutocompleteElement' as keyof GoogleMapsPlaces
  ] as GoogleMapsPlaces['PlaceAutocompleteElement'];
  if (PlaceAutocompleteElementCtor) {
    const widget = new PlaceAutocompleteElementCtor({
      includedRegionCodes: ['au'],
    });
    if (options?.placeholder) widget.placeholder = options.placeholder;
    if (options?.value) widget.value = options.value;
    widget.style.width = '100%';
    container.appendChild(widget);

    const onSelect = (event: Event) => {
      void (async () => {
        const prediction = (event as Event & { placePrediction?: PlacePredictionLike })
          .placePrediction;
        if (!prediction?.toPlace) return;
        try {
          const place = prediction.toPlace();
          await place.fetchFields({
            fields: ['formattedAddress', 'location', 'displayName'],
          });
          const formatted =
            place.formattedAddress?.trim() || place.displayName?.trim() || '';
          const coords = readLatLng(place.location ?? null);
          if (!formatted || !coords) return;
          handlers.onAddress(formatted);
          handlers.onCoordinates(coords);
        } catch {
          // Selection without resolvable coords — leave field unverified.
        }
      })();
    };

    widget.addEventListener('gmp-select', onSelect);
    widget.addEventListener('gmp-placeselect', onSelect);

    return () => {
      widget.removeEventListener('gmp-select', onSelect);
      widget.removeEventListener('gmp-placeselect', onSelect);
      widget.remove();
    };
  }

  // Classic Autocomplete fallback (legacy Places).
  if (!places.Autocomplete) return () => undefined;
  const input = document.createElement('input');
  input.type = 'text';
  input.className = 'ant-input';
  input.style.cssText =
    'width:100%;height:32px;padding:4px 11px;border:1px solid #d9d9d9;border-radius:6px;box-sizing:border-box;';
  input.placeholder = options?.placeholder ?? 'Search Google Maps…';
  if (options?.value) input.value = options.value;
  input.autocomplete = 'off';
  container.appendChild(input);
  return attachAddressAutocomplete(input, settings, handlers);
}

/**
 * Classic Places Autocomplete attached to an existing <input>.
 * Prefer {@link mountPlaceAutocompleteWidget} for new UI.
 */
export async function attachAddressAutocomplete(
  input: HTMLInputElement,
  settings: MapSettings,
  handlers: AddressAutocompleteHandlers
): Promise<() => void> {
  ensurePacStyles();

  const places = await getPlacesApi(settings);
  if (!places?.Autocomplete) return () => undefined;

  const autocomplete = new places.Autocomplete(input, {
    types: ['geocode'],
    componentRestrictions: { country: 'au' },
    fields: ['formatted_address', 'geometry', 'name', 'place_id'],
  });

  const listener = autocomplete.addListener('place_changed', () => {
    const place = autocomplete.getPlace();
    const formatted = place?.formatted_address?.trim() || place?.name?.trim();
    const location = place?.geometry?.location;
    if (!formatted) return;
    handlers.onAddress(formatted);
    if (location) {
      handlers.onCoordinates({ lat: location.lat(), lng: location.lng() });
    }
  });

  return () => {
    if (listener && typeof listener === 'object' && typeof listener.remove === 'function') {
      listener.remove();
    }
  };
}
